import {
  getApp,
  getApps,
  initializeApp,
  type FirebaseOptions
} from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type MessagePayload
} from "firebase/messaging";
import { createContext, useEffect, useMemo, useState } from "react";
import { BehaviorSubject, type Observable } from "rxjs";

import {
  FIREBASE_APIKEY,
  FIREBASE_APPID,
  FIREBASE_AUTHDOMAIN,
  FIREBASE_MEASUREMENTID,
  FIREBASE_MESSAGSENDINGID,
  FIREBASE_PREOJECTID,
  FIREBASE_STORAGEBUCKET,
  FIREBASE_VAPIDKEY
} from "@/data_source/constants/env";
import type { APINotificationType } from "@/data_source/models/APINotificationType";
import { NotificationUnreadCountRealtimeStore } from "@/stores/notification-unread-count-realtime-store";
import { UserStore } from "@/stores/user-store";
import { useObservable } from "@/utils/hook";
import { getActiveCompanySiteId } from "@/utils/notifications/getActiveCompanySiteId";
import {
  canBootstrapNotifications,
  type NotificationBootstrapStatus
} from "@/utils/notifications/notificationAccess";
import {
  resolveRealtimeUnreadCount,
  UNREAD_COUNT_EVENT_TYPE
} from "@/utils/notifications/unreadCountRealtime";
import request from "@/utils/request";

type FirebaseNotification = Partial<APINotificationType> & {
  time_since: string;
  icon: string;
  kind_display: string;
  severity_display: string;
  content_type: string;
  object_id: string | number;
};

const DEFAULT_NOTIFICATION_TITLE = "SiteHub";
const DEFAULT_NOTIFICATION_SEVERITY: FirebaseNotification["severity"] = "info";

function normalizeFirebaseMessage(
  payload: MessagePayload
): FirebaseNotification {
  const data = payload.data ?? {};
  const notification = payload.notification;

  return {
    alarm_type: Number(data.alarm_type ?? 0),
    content_type: data.content_type ?? "",
    created: data.created ?? new Date().toISOString(),
    icon: data.icon ?? "/favicon.ico",
    id: Number(data.id ?? 0),
    kind: (data.kind as FirebaseNotification["kind"]) ?? "notification",
    kind_display: data.kind_display ?? "",
    message: notification?.body ?? data.message ?? data.body ?? "",
    modified: data.modified ?? new Date().toISOString(),
    object_id: data.object_id ?? "",
    read: String(data.read).match(/^true$/i) ? true : false,
    severity:
      (data.severity as FirebaseNotification["severity"]) ??
      DEFAULT_NOTIFICATION_SEVERITY,
    severity_display: data.severity_display ?? "",
    subject:
      notification?.title ??
      data.subject ??
      data.title ??
      DEFAULT_NOTIFICATION_TITLE,
    time_since: data.time_since ?? "",
    type: (data.type as FirebaseNotification["type"]) ?? "g",
    user: Number(data.user ?? 0),
    visible: String(data.visible).match(/^false$/i) ? false : true,
    workflow: Number(data.workflow ?? 0)
  };
}

export const firebaseContext = createContext<{
  status: NotificationBootstrapStatus;
  $messages: Observable<FirebaseNotification | null>;
}>({
  status: "disconnected",
  $messages: new BehaviorSubject<FirebaseNotification | null>(null)
});

type Props = React.PropsWithChildren;

async function loadFirebaseWorker() {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service workers are not supported in this browser.");
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js"
    );
    console.log("[firebase] Service worker registered", registration.scope);
    return registration;
  } catch (error) {
    console.error("[firebase] Error registering service worker", error);
    return null;
  }
}

export function AppFirebaseNotificationProvider(props: Props) {
  const [status, setStatus] =
    useState<NotificationBootstrapStatus>("disconnected");
  const $messages = useMemo(
    () => new BehaviorSubject<FirebaseNotification | null>(null),
    []
  );
  const [user] = useObservable(UserStore.getInstance().observable);
  const canInitializeMessaging = canBootstrapNotifications(
    user?.access_control
  );
  const firebaseConfig: FirebaseOptions = useMemo(
    () => ({
      apiKey: FIREBASE_APIKEY,
      authDomain: FIREBASE_AUTHDOMAIN,
      projectId: FIREBASE_PREOJECTID,
      storageBucket: FIREBASE_STORAGEBUCKET,
      messagingSenderId: FIREBASE_MESSAGSENDINGID,
      appId: FIREBASE_APPID,
      measurementId: FIREBASE_MEASUREMENTID
    }),
    []
  );
  useEffect(() => {
    if (!canInitializeMessaging) {
      setStatus("disconnected");
      $messages.next(null);
      return;
    }

    let isActive = true;
    let messageUnsubscribe: (() => void) | undefined;
    let deviceRequest:
      | ReturnType<ReturnType<typeof request.postRequest>>
      | undefined;

    const initializeMessaging = async () => {
      try {
        if (typeof Notification === "undefined") {
          console.warn(
            "Firebase Messaging: Notification API not supported in this browser"
          );
          setStatus("disconnected");
          return;
        }

        const registration = await loadFirebaseWorker();
        if (!isActive || !registration) {
          setStatus("disconnected");
          return;
        }

        const messagingSupported = await isSupported();
        if (!isActive || !messagingSupported) {
          console.warn("Firebase Messaging: Not supported in this browser");
          setStatus("disconnected");
          return;
        }

        setStatus("waiting");
        const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
        const messaging = getMessaging(app);
        const permission = await Notification.requestPermission();
        if (!isActive) return;
        if (permission !== "granted") {
          setStatus(
            permission === "denied" ? "permission-denied" : "disconnected"
          );
          return;
        }

        const token = await getToken(messaging, {
          vapidKey: FIREBASE_VAPIDKEY,
          serviceWorkerRegistration: registration
        });
        if (!isActive) return;
        if (!token) throw Error("Firebase Messaging did not return a token");

        setStatus("connected");
        deviceRequest = request.postRequest(
          "api/fcm_devices/",
          {
            registration_id: token,
            type: "web",
            name: navigator.userAgent
          },
          {
            "content-type": "application/json"
          }
        )();
        deviceRequest.response.catch((error) => {
          if (!isActive || error instanceof DOMException) return;
          setStatus("error");
          console.error("[firebase] Device registration error", error);
        });

        messageUnsubscribe = onMessage(messaging, (payload) => {
          console.log("[firebase] Foreground message received", payload);
          const data = payload.data ?? {};

          // Realtime unread-count events are data-only and must not be rendered
          // as notifications; they only update the bell badge.
          if (data.type === UNREAD_COUNT_EVENT_TYPE) {
            const unreadCount = resolveRealtimeUnreadCount(
              data,
              getActiveCompanySiteId()
            );
            if (unreadCount !== null) {
              NotificationUnreadCountRealtimeStore.getInstance().add({
                unreadCount,
                receivedAt: Date.now()
              });
            }
            return;
          }

          $messages.next(normalizeFirebaseMessage(payload));
        });
      } catch (error) {
        if (!isActive || error instanceof DOMException) return;
        console.error("Firebase Messaging: Error during initialization", error);
        setStatus("error");
      }
    };

    initializeMessaging();

    return () => {
      isActive = false;
      messageUnsubscribe?.();
      deviceRequest?.abort();
    };
  }, [$messages, canInitializeMessaging, firebaseConfig]);
  return (
    <firebaseContext.Provider
      value={{
        status,
        $messages
      }}
    >
      {props.children}
    </firebaseContext.Provider>
  );
}
